import React, { useEffect, useState } from 'react';
import { Menu, MenuHandler, MenuList, MenuItem, Avatar } from "@material-tailwind/react";
import { Bars3Icon, ArrowRightOnRectangleIcon, ClockIcon } from '@heroicons/react/24/outline';
import pkg from '../../../../package.json'
import { useSelector, useDispatch } from 'react-redux';
import { setToggleSidebar } from '../../../reducers/contextSlice';
import { get_config_link, logout } from '../../../Controller/rtkActions';
import { Link } from 'react-router-dom'
import { IoLogoGooglePlaystore } from 'react-icons/io5';
import { FiGlobe } from "react-icons/fi";

export const Header = () => {
    const dispatch = useDispatch()
    const { toggleSidebar, expiryTime, configData, deleteUpdateTag, deleteUpdateYes } = useSelector((state) => state.context);
    const [minutes, setMinutes] = useState('00');
    const [seconds, setSeconds] = useState('00');
    const [timeLimit, setTimeLimit] = useState(1)

    useEffect(() => {
        if (expiryTime !== null) {
            setTimeLimit(Math.floor(expiryTime / 1000) - Math.floor(Date.now() / 1000));
        }
    }, [expiryTime]);

    useEffect(() => {
        let timerId;
        if (timeLimit > 0) {
            timerId = setInterval(() => {
                setTimeLimit((prevTimeLimit) => Math.max(0, prevTimeLimit - 1))
            }, 1000)
        } else {
            dispatch(logout());
        }

        return () => clearInterval(timerId)
    }, [timeLimit])

    useEffect(() => {
        setMinutes(Math.floor(timeLimit / 60).toString().padStart(2, '0'))
        setSeconds(Math.floor(timeLimit % 60).toString().padStart(2, '0'))
    }, [timeLimit])

    useEffect(() => {
        dispatch(get_config_link())
    }, [deleteUpdateTag, deleteUpdateYes])

    const playStoreLink = configData?.find(item => item?.title === 'PlayStore');
    const webLink = configData?.find(item => item?.title === 'Webpages');

    return (
        <header className="sticky top-0 z-60 w-full bg-white border-b border-border">
            <div className="flex items-center justify-between h-16 px-6">
                {/* Left section: Mobile menu + External links */}
                <div className='flex items-center gap-3'>
                    {/* Mobile menu toggle */}
                    <button
                        onClick={() => dispatch(setToggleSidebar(!toggleSidebar))}
                        className="2xl:hidden p-2 rounded-lg hover:bg-background-hover transition-colors"
                        aria-label="Toggle menu"
                    >
                        <Bars3Icon className="h-5 w-5 text-text-secondary" />
                    </button>

                    {/* Mobile logo */}
                    <div className="flex items-center gap-2 2xl:hidden">
                        <img 
                            src="../../assets/images/foto-ai-logo.svg" 
                            className='h-8' 
                            alt="Foto AI Logo" 
                        />
                        <span className='text-xs font-semibold text-text-tertiary'>
                            v{pkg.version}
                        </span>
                    </div>

                    {/* External links */}
                    <div className='hidden md:flex items-center gap-2'>
                        {playStoreLink && (
                            <Link 
                                to={playStoreLink.description} 
                                target='_blank'
                                className='flex items-center justify-center w-9 h-9 rounded-lg bg-background-muted hover:bg-background-hover text-text-secondary hover:text-text-primary transition-colors'
                                aria-label="Play Store"
                            >
                                <IoLogoGooglePlaystore className="text-lg" />
                            </Link>
                        )}
                        {webLink && (
                            <Link 
                                to={webLink.description} 
                                target='_blank'
                                className='flex items-center justify-center w-9 h-9 rounded-lg bg-background-muted hover:bg-background-hover text-text-secondary hover:text-text-primary transition-colors'
                                aria-label="Website"
                            >
                                <FiGlobe className="text-lg" />
                            </Link>
                        )}
                    </div>
                </div>

                {/* Right section: Session timer + Profile */}
                <div className='flex items-center gap-4'>
                    {/* Session countdown timer */}
                    <div className='hidden sm:flex items-center gap-3 px-3 py-2 rounded-lg bg-background-muted border border-border'>
                        <ClockIcon className="h-4 w-4 text-text-tertiary" />
                        <div className='flex items-center gap-1'>
                            <span className='text-xs font-medium text-text-secondary'>Session:</span>
                            <div className='flex items-center gap-1 font-mono'>
                                <span className='text-xs font-semibold text-text-primary bg-white px-1.5 py-0.5 rounded border border-border'>
                                    {minutes?.split('')[0]}
                                </span>
                                <span className='text-xs font-semibold text-text-primary bg-white px-1.5 py-0.5 rounded border border-border'>
                                    {minutes?.split('')[1]}
                                </span>
                                <span className='text-xs text-text-tertiary mx-0.5'>:</span>
                                <span className='text-xs font-semibold text-text-primary bg-white px-1.5 py-0.5 rounded border border-border'>
                                    {seconds?.split('')[0]}
                                </span>
                                <span className='text-xs font-semibold text-text-primary bg-white px-1.5 py-0.5 rounded border border-border'>
                                    {seconds?.split('')[1]}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Profile Menu */}
                    <Menu placement='bottom-end'>
                        <MenuHandler>
                            <button className='flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-background-hover transition-colors group'>
                                <Avatar
                                    variant="circular"
                                    alt={localStorage.getItem('userName')}
                                    className="w-8 h-8 ring-2 ring-transparent group-hover:ring-primary-100 transition-all"
                                    src="https://images.unsplash.com/photo-1633332755192-727a05c4013d?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1480&q=80"
                                />
                                <div className='hidden lg:block text-left'>
                                    <div className="text-sm font-medium text-text-primary capitalize">
                                        {localStorage.getItem('userName')}
                                    </div>
                                    <div className="text-xs text-text-tertiary">
                                        Administrator
                                    </div>
                                </div>
                            </button>
                        </MenuHandler>
                        <MenuList className="min-w-[200px] p-2 border-border shadow-lg">
                            <div className="px-3 py-2 border-b border-border mb-2">
                                <div className="text-sm font-medium text-text-primary capitalize">
                                    {localStorage.getItem('userName')}
                                </div>
                                <div className="text-xs text-text-tertiary mt-0.5">
                                    Admin Account
                                </div>
                            </div>
                            <MenuItem 
                                onClick={() => { dispatch(logout()); }}
                                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-text-secondary hover:text-error-DEFAULT hover:bg-error-light transition-colors"
                            >
                                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                                <span>Logout</span>
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </div>
            </div>
        </header>
    )
}

export default Header